import { useMemo, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, ShieldCheck, AlertCircle } from 'lucide-react';
import { LoginInput } from '@/shared/types';
import { useLogin } from '@/hooks/useAuth';
import { useSession } from '@/stores/session';
import { ApiError } from '@/lib/api';

// Frases breves para el panel del hero. Una se elige al azar al cargar la
// página. Mantener el tono: profesional, cálido, sin cursi, sin sermón.
const HERO_MESSAGES = [
  'Cada pago registrado, un compromiso cumplido.',
  'Lo que se mide, mejora.',
  'Las cuentas claras sostienen relaciones largas.',
  'Hoy también es buen día para ponerse al día.',
  'Los detalles construyen la confianza.',
  'El orden es una forma de cuidar.',
  'Pequeños pasos, grandes resultados.',
  'Cuidar los números es cuidar el futuro.',
  'Una decisión a tiempo vale por mil después.',
  'Tu trabajo sostiene más de lo que se ve.',
  'La calma se nota en los números.',
  'Constancia y precisión: tu superpoder diario.',
  'Hacer simple lo complejo es el verdadero arte.',
  'Antes de cerrar el día, una cuenta más.',
  'Cobrar es cuidar. Auditar es proteger.',
  'Cada acción cuenta. Cada registro suma.',
  'Empieza por una. El resto sigue.',
  'La confianza se construye dato por dato.',
  'Disciplina hoy, tranquilidad mañana.',
  'Llevar el control también es liderar.',
];

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const user = useSession((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pick one once per mount so the message doesn't change while typing.
  const heroMessage = useMemo(
    () => HERO_MESSAGES[Math.floor(Math.random() * HERO_MESSAGES.length)],
    [],
  );

  if (user) {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = LoginInput.safeParse({ email: email.trim(), password });
    if (!parsed.success) {
      setError('Email o contraseña inválidos.');
      return;
    }
    try {
      await login.mutateAsync(parsed.data);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError('Credenciales incorrectas.');
        else if (err.status === 429) setError('Demasiados intentos. Espera unos minutos.');
        else setError('No pudimos iniciar sesión. Intenta de nuevo.');
      } else {
        setError('Error de red. Revisa tu conexión.');
      }
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-5xl grid-cols-1 overflow-hidden rounded-2xl border border-ink-600/70 bg-ink-800/70 shadow-card lg:grid-cols-2">
        {/* Hero panel */}
        <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-ink-700 via-ink-800 to-ink-900 p-10 lg:flex">
          <div className="flex items-center gap-2 text-mustard-400">
            <ShieldCheck className="h-6 w-6" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">
              Datacole Central
            </span>
          </div>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <div className="text-xs font-medium uppercase tracking-[0.25em] text-mustard-400/80">
                Área de
              </div>
              <h1 className="text-5xl font-bold leading-none tracking-tight text-moss-200">
                Tesorería
              </h1>
            </div>
            <p className="max-w-sm border-l-2 border-mustard-500/50 pl-4 text-sm italic leading-relaxed text-moss-300/90">
              {heroMessage}
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-moss-300/70">
            <span className="h-2 w-2 rounded-full bg-ok" />
            Conexión cifrada · Auditoría completa · Acceso por rol
          </div>

          {/* decorative glow */}
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-mustard-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -top-24 -left-16 h-72 w-72 rounded-full bg-teal/10 blur-3xl" />
        </aside>

        {/* Form panel */}
        <section className="flex flex-col justify-center p-8 sm:p-12">
          <header className="mb-8 space-y-1.5">
            <h2 className="text-2xl font-semibold text-moss-200">Inicia sesión</h2>
            <p className="text-sm text-moss-300/70">
              Accede con tu cuenta corporativa para gestionar la cobranza.
            </p>
          </header>

          <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-moss-300">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base"
                placeholder="tu@empresa.com"
                disabled={login.isPending}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-moss-300">
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base"
                placeholder="••••••••"
                disabled={login.isPending}
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={login.isPending} className="btn-primary w-full">
              <LogIn className="h-4 w-4" />
              {login.isPending ? 'Verificando…' : 'Entrar'}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-moss-300/60">
            ¿Olvidaste tu contraseña? Contacta al administrador del sistema.
          </p>
        </section>
      </div>
    </main>
  );
}
